/**
 * LoopNest Shared Video Pipeline Utilities
 * Reusable across Music-to-Video, Text-to-Video, Image-to-Video, Script-to-Video, Lyrics Video
 */

// ─── SAFE MEDIA SOURCING ───────────────────────────────────────────────────

export const APPROVED_PROVIDERS = [
  { id: 'user-upload', label: 'User Upload', license: 'user-owned', attribution: false },
  { id: 'loopnest-generated', label: 'LoopNest AI Generated', license: 'cc0-equivalent', attribution: false },
  { id: 'cc0-generated', label: 'CC0 Generated', license: 'cc0', attribution: false },
  { id: 'unsplash-free', label: 'Unsplash Free', license: 'unsplash-license', attribution: true },
  { id: 'pexels-free', label: 'Pexels Free', license: 'pexels-license', attribution: true },
  { id: 'pixabay-cc0', label: 'Pixabay CC0', license: 'cc0', attribution: false },
];

const APPROVED_PROVIDER_IDS = new Set(APPROVED_PROVIDERS.map((p) => p.id));

export const SUSPICIOUS_FILENAME_PATTERNS = [
  'shutterstock', 'getty', 'istock', 'adobe-stock', 'dreamstime',
  'depositphotos', 'alamy', 'bigstock', 'watermark', 'licensed',
  'copyright', 'getty_images', 'stock_photo', 'rf_', '_rf.',
];

/**
 * Verify a media provider is approved for use in LoopNest
 */
export function verifyProvider(providerId) {
  return APPROVED_PROVIDER_IDS?.has(providerId);
}

/**
 * Detect suspicious/watermarked media from filename patterns
 */
export function detectSuspiciousMedia(file) {
  if (!file) return false;
  const name = (file?.name || '')?.toLowerCase();
  return SUSPICIOUS_FILENAME_PATTERNS?.some((p) => name?.includes(p));
}

/**
 * Build source metadata record for a media item
 */
export function buildSourceMetadata(providerId, file = null) {
  const provider = APPROVED_PROVIDERS?.find((p) => p?.id === providerId);
  return {
    providerId,
    providerLabel: provider?.label || 'Unknown',
    license: provider?.license || 'unknown',
    requiresAttribution: provider?.attribution || false,
    fileName: file?.name || null,
    fileSize: file?.size || null,
    importedAt: new Date()?.toISOString(),
    verified: verifyProvider(providerId),
  };
}

// ─── DURATION MANAGEMENT ──────────────────────────────────────────────────

export const DURATION_LIMITS = {
  free: { max: 120, label: 'Free (max 2 min)' },
  pro: { max: 300, label: 'Pro (max 5 min)' },
  studio: { max: 600, label: 'Studio (max 10 min)' },
  enterprise: { max: Infinity, label: 'Enterprise (unlimited)' },
};

/**
 * Calculate effective render duration based on mode, user tier, and test mode
 */
export function getEffectiveDuration(audioDuration, durationMode, customDuration, userTier, testMode) {
  let raw;
  if (durationMode === 'full') raw = audioDuration;
  else if (durationMode === '30s') raw = Math.min(30, audioDuration);
  else if (durationMode === '60s') raw = Math.min(60, audioDuration);
  else if (durationMode === 'custom') raw = Math.min(customDuration, audioDuration);
  else raw = audioDuration;

  if (testMode) return raw; // Test mode: no cap

  const limit = DURATION_LIMITS?.[userTier]?.max ?? DURATION_LIMITS?.free?.max;
  return Math.min(raw, limit);
}

/**
 * Check if a duration mode requires a specific tier
 */
export function isDurationLocked(durationMode, userTier, testMode) {
  if (testMode) return false;
  if (durationMode === 'custom') return userTier !== 'studio' && userTier !== 'enterprise';
  return false;
}

// ─── CAPTION ENGINE ───────────────────────────────────────────────────────

/**
 * Parse lyrics text into timed lines
 * Supports:
 *   [00:12.34] Line text  (LRC format)
 *   Plain text lines (auto-timed)
 */
export function parseLyrics(text, totalDuration) {
  if (!text?.trim()) return [];
  const lines = text?.split('\n')?.filter((l) => l?.trim());

  // Try LRC timestamp format
  const lrcPattern = /^\[(\d{2}):(\d{2})(?:\.(\d{2}))?\](.*)$/;
  const timed = lines?.map((line) => {
    const match = line?.match(lrcPattern);
    if (match) {
      const mins = parseInt(match?.[1], 10);
      const secs = parseInt(match?.[2], 10);
      const cs = parseInt(match?.[3] || '0', 10);
      return { time: mins * 60 + secs + cs / 100, text: match?.[4]?.trim() };
    }
    return null;
  });

  const hasTimestamps = timed?.some((t) => t !== null);
  if (hasTimestamps) {
    return timed?.filter(Boolean);
  }

  // Auto-distribute lines evenly across duration
  const dur = totalDuration || lines?.length * 3;
  const interval = dur / lines?.length;
  return lines?.map((text, i) => ({ time: i * interval, text }));
}

/**
 * Get the current lyric line for a given playback time
 */
export function getCurrentLyricLine(parsedLines, currentTime) {
  if (!parsedLines?.length) return null;
  let current = parsedLines?.[0];
  for (const line of parsedLines) {
    if (line?.time <= currentTime) current = line;
    else break;
  }
  return current;
}

/**
 * Get next lyric line (for karaoke lookahead)
 */
export function getNextLyricLine(parsedLines, currentTime) {
  if (!parsedLines?.length) return null;
  for (const line of parsedLines) {
    if (line?.time > currentTime) return line;
  }
  return null;
}

// ─── AI MEDIA RETRIEVAL ───────────────────────────────────────────────────

// Semantic keyword mapping for visual retrieval
const THEME_VISUAL_MAP = {
  worship: ['sky', 'light', 'heaven', 'peaceful', 'stars', 'sunrise', 'clouds', 'faith', 'hope'],
  spiritual: ['light', 'stars', 'peaceful', 'nature', 'sky', 'sunrise', 'clouds'],
  love: ['sunset', 'couple', 'flowers', 'warm light', 'golden hour', 'romance'],
  nature: ['forest', 'mountains', 'ocean', 'waterfall', 'meadow', 'trees', 'sky'],
  urban: ['city', 'neon', 'street', 'skyline', 'night city', 'architecture'],
  cinematic: ['dramatic sky', 'cinematic landscape', 'epic vista', 'golden hour', 'fog'],
  dark: ['night', 'shadow', 'storm', 'dark forest', 'moonlight'],
  energetic: ['fire', 'lightning', 'crowd', 'concert', 'motion blur', 'speed'],
  peaceful: ['calm ocean', 'meadow', 'soft light', 'morning mist', 'gentle waves'],
  gospel: ['light rays', 'church', 'sky', 'hope', 'sunrise', 'community'],
  afrobeat: ['vibrant colors', 'dance', 'celebration', 'africa', 'rhythm'],
  electronic: ['neon', 'abstract', 'digital', 'circuit', 'futuristic'],
  ambient: ['fog', 'mist', 'soft light', 'abstract', 'slow motion water'],
  documentary: ['real people', 'landscape', 'authentic', 'candid', 'storytelling'],
};

/**
 * Extract semantic keywords from song metadata for visual retrieval
 */
export function extractVisualKeywords(params) {
  const { title = '', prompt = '', mood = '', genre = '', lyrics = '' } = params;
  const combined = `${title} ${prompt} ${mood} ${genre} ${lyrics}`?.toLowerCase();
  const keywords = new Set();

  // Direct theme matching
  Object.entries(THEME_VISUAL_MAP)?.forEach(([theme, visuals]) => {
    if (combined?.includes(theme)) {
      visuals?.forEach((v) => keywords?.add(v));
    }
  });

  // Extract significant words (nouns/adjectives)
  const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'shall', 'can', 'need', 'dare', 'ought', 'used', 'i', 'me', 'my', 'you', 'your', 'we', 'our', 'they', 'their', 'it', 'its']);
  const words = combined?.split(/\s+/)?.filter((w) => w?.length > 3 && !stopWords?.has(w));
  words?.slice(0, 8)?.forEach((w) => keywords?.add(w));

  return Array.from(keywords)?.slice(0, 12);
}

/**
 * Build Unsplash-compatible search query from keywords
 * Returns safe, approved search terms for CC0/free-use providers
 */
export function buildSafeSearchQuery(keywords, styleId) {
  const styleModifiers = {
    'realistic-photos': 'cinematic photography',
    'cinematic-stock': 'cinematic dramatic',
    'nature': 'nature landscape',
    'urban': 'city urban architecture',
    'documentary': 'documentary authentic',
    'worship': 'spiritual light peaceful',
    'realistic-captions': 'cinematic photography',
    'realistic-beats': 'dynamic motion',
    'visualizer-cinematic': 'cinematic abstract',
  };
  const modifier = styleModifiers?.[styleId] || '';
  const query = [...keywords?.slice(0, 4), modifier]?.filter(Boolean)?.join(' ');
  return query || 'cinematic landscape';
}

/**
 * Generate Unsplash-sourced image URL (free tier, no API key needed for display)
 * Uses Unsplash Source API which provides CC0-licensed images
 */
export function getUnsplashImageUrl(query, width = 1280, height = 720) {
  const encoded = encodeURIComponent(query);
  return `https://source.unsplash.com/${width}x${height}/?${encoded}`;
}

/**
 * Generate a sequence of visual scene descriptors for a given duration
 */
export function generateSceneTimeline(keywords, duration, styleId) {
  const sceneCount = Math.max(3, Math.ceil(duration / 15)); // ~1 scene per 15 seconds
  const scenes = [];
  for (let i = 0; i < sceneCount; i++) {
    const keyword = keywords?.[i % keywords?.length] || 'cinematic';
    const startTime = (i / sceneCount) * duration;
    const endTime = ((i + 1) / sceneCount) * duration;
    scenes?.push({
      index: i,
      startTime,
      endTime,
      duration: endTime - startTime,
      keyword,
      query: buildSafeSearchQuery([keyword], styleId),
      imageUrl: getUnsplashImageUrl(buildSafeSearchQuery([keyword], styleId)),
      transition: i % 3 === 0 ? 'fade' : i % 3 === 1 ? 'dissolve' : 'cut',
    });
  }
  return scenes;
}

// ─── AUDIO ANALYSIS ───────────────────────────────────────────────────────

/**
 * Analyze audio buffer: extract duration, waveform, BPM
 */
export async function analyzeAudioBuffer(arrayBuffer) {
  const offlineCtx = new OfflineAudioContext(1, 1, 44100);
  const audioBuffer = await offlineCtx?.decodeAudioData(arrayBuffer);
  const dur = audioBuffer?.duration;

  const rawData = audioBuffer?.getChannelData(0);
  const samples = 100;
  const blockSize = Math.floor(rawData?.length / samples);
  const waveform = [];
  for (let i = 0; i < samples; i++) {
    let sum = 0;
    for (let j = 0; j < blockSize; j++) sum += Math.abs(rawData?.[i * blockSize + j]);
    waveform?.push(sum / blockSize);
  }

  // BPM estimation via energy peaks
  const sampleRate = audioBuffer?.sampleRate;
  const windowSize = Math.floor(sampleRate * 0.1);
  const energies = [];
  for (let i = 0; i < rawData?.length - windowSize; i += windowSize) {
    let e = 0;
    for (let j = 0; j < windowSize; j++) e += rawData?.[i + j] ** 2;
    energies?.push(e / windowSize);
  }
  const avgEnergy = energies?.reduce((a, b) => a + b, 0) / energies?.length;
  let beats = 0;
  for (let i = 1; i < energies?.length - 1; i++) {
    if (energies?.[i] > avgEnergy * 1.5 && energies?.[i] > energies?.[i - 1] && energies?.[i] > energies?.[i + 1]) beats++;
  }
  const bpm = Math.min(200, Math.max(60, Math.round((beats / dur) * 60)));

  return { duration: dur, waveform, bpm, sampleRate };
}

// ─── CANVAS DRAWING HELPERS ───────────────────────────────────────────────

/**
 * Draw caption overlay on canvas with multiple style modes
 */
export function drawCaptionOverlay(ctx, w, h, parsedLines, currentTime, captionMode, captionStyle = {}) {
  if (!parsedLines?.length || captionMode === 'none') return;

  const currentLine = getCurrentLyricLine(parsedLines, currentTime);
  if (!currentLine?.text?.trim()) return;

  const {
    fontSize = captionMode === 'cinematic' ? 36 : captionMode === 'animated' ? 32 : 28,
    fontFamily = 'sans-serif',
    colorTheme = 'white',
    placement = 'bottom',
  } = captionStyle;

  const colorMap = {
    white: { text: '#ffffff', bg: 'rgba(0,0,0,0.55)', highlight: '#F43F5E' },
    yellow: { text: '#FFE600', bg: 'rgba(0,0,0,0.6)', highlight: '#FF6B00' },
    cyan: { text: '#00E5FF', bg: 'rgba(0,0,10,0.6)', highlight: '#8B5CF6' },
    gold: { text: '#F0E6C8', bg: 'rgba(0,0,0,0.5)', highlight: '#F59E0B' },
  };
  const colors = colorMap?.[colorTheme] || colorMap?.white;

  const yPos = placement === 'center' ? h / 2 : h - 60;

  ctx.font = `bold ${fontSize}px ${fontFamily}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const line = currentLine?.text;
  const tw = ctx?.measureText(line)?.width;

  if (captionMode === 'karaoke') {
    ctx.shadowColor = colors?.highlight;
    ctx.shadowBlur = 20;
    ctx.fillStyle = colors?.bg;
    ctx?.fillRect(w / 2 - tw / 2 - 20, yPos - fontSize / 2 - 10, tw + 40, fontSize + 20);
    ctx.fillStyle = colors?.text;
    ctx?.fillText(line, w / 2, yPos);
    ctx.shadowBlur = 0;
  } else if (captionMode === 'cinematic') {
    // Full-width letterbox bar
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx?.fillRect(0, yPos - fontSize / 2 - 14, w, fontSize + 28);
    ctx.fillStyle = colors?.gold || colors?.text;
    ctx?.fillText(line, w / 2, yPos);
  } else if (captionMode === 'animated') {
    // Glow + scale pulse
    const pulse = 1 + Math.sin(Date.now() * 0.005) * 0.03;
    ctx?.save();
    ctx?.translate(w / 2, yPos);
    ctx?.scale(pulse, pulse);
    ctx.shadowColor = colors?.highlight;
    ctx.shadowBlur = 25;
    ctx.fillStyle = colors?.bg;
    ctx?.fillRect(-tw / 2 - 16, -fontSize / 2 - 8, tw + 32, fontSize + 16);
    ctx.fillStyle = colors?.text;
    ctx?.fillText(line, 0, 0);
    ctx.shadowBlur = 0;
    ctx?.restore();
  } else {
    // Simple subtitles
    ctx.fillStyle = colors?.bg;
    ctx?.fillRect(w / 2 - tw / 2 - 12, yPos - fontSize / 2 - 8, tw + 24, fontSize + 16);
    ctx.fillStyle = colors?.text;
    ctx?.fillText(line, w / 2, yPos);
  }

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
}

/**
 * Format seconds to mm:ss display
 */
export function formatDuration(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = String(Math.floor(seconds % 60))?.padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Format seconds to human-readable label
 */
export function formatDurationLabel(seconds) {
  if (!seconds) return null;
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

/**
 * Save a creation to localStorage library
 */
export function saveCreationToLibrary(creation, storageKey = 'gameforge_games') {
  try {
    const stored = localStorage.getItem(storageKey);
    const existing = stored ? JSON.parse(stored) : [];
    const updated = [creation, ...existing?.filter((c) => c?.id !== creation?.id)];
    localStorage.setItem(storageKey, JSON.stringify(updated));
    return true;
  } catch {
    return false;
  }
}
